/**
 * Sort findings by severity and rank
 * @param {Array} findings - Array of findings to sort
 * @returns {Array} Sorted findings
 */
export const sortFindingsBySeverity = (findings) => {
  const severityOrder = {
    'critical': 1,
    'high': 2,
    'medium': 3,
    'low': 4,
    'informational': 5,
    'insight': 6
  };

  return [...findings].sort((a, b) => {
    // Case insensitive comparison
    const severityA = a.severity?.toLowerCase() || 'informational';
    const severityB = b.severity?.toLowerCase() || 'informational';
    
    return severityOrder[severityA] - severityOrder[severityB];
  });
};

/**
 * Process contest data to extract top findings
 * @param {Array} contests - Array of contest objects
 * @param {number} limit - Maximum number of findings to return
 * @returns {Array} Processed findings data
 */
export const processContestFindings = (contests, limit = 5) => {
  // Filter contests with published findings and sort by significance
  const significantContests = contests
    .filter(contest => contest.isPublished && contest.findings)
    .sort((a, b) => {
      // Sort by critical findings first, then high, then rank
      const aHasCritical = a.findings.includes('C');
      const bHasCritical = b.findings.includes('C');
      
      if (aHasCritical && !bHasCritical) return -1;
      if (!aHasCritical && bHasCritical) return 1;
      
      const aHighCount = (a.findings.match(/\d+H/g) || []).reduce((sum, match) => sum + parseInt(match), 0);
      const bHighCount = (b.findings.match(/\d+H/g) || []).reduce((sum, match) => sum + parseInt(match), 0);
      
      if (aHighCount !== bHighCount) return bHighCount - aHighCount;
      
      // If tied on findings, sort by rank (1st is better than 2nd)
      const aRank = a.rank.includes('1st') ? 1 : a.rank.includes('2nd') ? 2 : a.rank.includes('3rd') ? 3 : 4;
      const bRank = b.rank.includes('1st') ? 1 : b.rank.includes('2nd') ? 2 : b.rank.includes('3rd') ? 3 : 4;
      
      return aRank - bRank;
    })
    .slice(0, limit);
    
  return significantContests.map(contest => ({
    id: contest.id || `${contest.project}-${contest.date}`,
    project: contest.project,
    findings: contest.findings,
    platform: contest.platform,
    platformURL: contest.platformURL || contest.platformUrl,
    projectURL: contest.projectURL || contest.projectUrl,
    reportURL: contest.reportURL || contest.reportUrl,
    myFindings: contest.myFindings || [],
    rank: contest.rank
  }));
};

/**
 * Sort data by date (newest first)
 * @param {Array} data - Array of objects with date property
 * @returns {Array} Sorted data
 */
export const sortByDate = (data) => {
  return [...data].sort((a, b) => {
    const dateA = a.date || 0;
    const dateB = b.date || 0;
    return dateB - dateA;
  });
};

/**
 * Get award class and label based on award value
 * @param {number|string} award - Award value (1, 2, 3 or 'gold', 'silver', etc.)
 * @returns {Object} Object with awardClass and awardLabel properties
 */
export const getAwardInfo = (award) => {
  let awardClass = '';
  let awardLabel = '';
  
  if (typeof award === 'number') {
    // If award is a number (1, 2, 3)
    if (award === 1) {
      awardClass = 'gold';
      awardLabel = '1st 🥇';
    } else if (award === 2) {
      awardClass = 'silver';
      awardLabel = '2nd 🥈';
    } else if (award === 3) {
      awardClass = 'bronze';
      awardLabel = '3rd 🥉';
    } else {
      awardClass = 'participant';
      awardLabel = `${award}th`;
    }
  } else {
    // If award is already a string
    awardClass = award || 'participant';
    awardLabel = award === 'gold' ? '1st 🥇' : 
                 award === 'silver' ? '2nd 🥈' : 
                 award === 'bronze' ? '3rd 🥉' : 
                 'Participant';
  }
  
  return { awardClass, awardLabel };
};
